import { Box, Grid } from "@mui/material";
import { factories, models, service } from "powerbi-client";
import { useCallback, useEffect, useRef, useState } from "react";
import Loader from "../components/general/Loader";
import NunitoText from "../components/general/NunitoText";
import NoData from "../components/general/NoData";
import { getPowerBiEmbedToken } from "../functions/getData";

const powerbi = new service.Service(
  factories.hpmFactory,
  factories.wpmpFactory,
  factories.routerFactory
);

const isValidEmbedConfig = (data) =>
  data &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  data.embedUrl &&
  data.embedToken &&
  data.reportId;

const buildEmbedConfiguration = (config) => ({
  type: "report",
  id: config.reportId,
  embedUrl: config.embedUrl,
  accessToken: config.embedToken,
  tokenType: models.TokenType.Embed,
  settings: {
    panes: {
      filters: { expanded: false, visible: false },
      pageNavigation: { visible: true },
    },
    background: models.BackgroundType.Transparent,
  },
});

const getRefreshDelayMs = (config) => {
  const minutes = Number(config?.expiresInMinutes);
  if (!Number.isFinite(minutes) || minutes <= 5) {
    return null;
  }
  return (minutes - 5) * 60 * 1000;
};

export default function Dashboard() {
  document.title = "Dashboard";
  const containerRef = useRef(null);
  const reportRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const resetEmbed = useCallback(() => {
    if (containerRef.current) {
      powerbi.reset(containerRef.current);
    }
    reportRef.current = null;
  }, []);

  const embedReport = useCallback(
    async (config) => {
      if (!containerRef.current || !isValidEmbedConfig(config)) {
        return;
      }

      if (reportRef.current) {
        await reportRef.current.setAccessToken(config.embedToken);
        return;
      }

      resetEmbed();
      reportRef.current = powerbi.embed(
        containerRef.current,
        buildEmbedConfiguration(config)
      );
    },
    [resetEmbed]
  );

  const scheduleTokenRefresh = useCallback(
    (refreshFn) => {
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(refreshFn, 55 * 60 * 1000);
    },
    [clearRefreshTimer]
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError("");
        const config = await getPowerBiEmbedToken("", controller.signal);
        if (!active) {
          return;
        }
        if (!isValidEmbedConfig(config)) {
          throw new Error("Invalid Power BI embed configuration.");
        }

        await embedReport(config);
        if (!active) {
          return;
        }

        const delayMs = getRefreshDelayMs(config);
        if (delayMs) {
          refreshTimerRef.current = setTimeout(async () => {
            if (!active) {
              return;
            }
            try {
              const refreshedConfig = await getPowerBiEmbedToken("");
              if (active && isValidEmbedConfig(refreshedConfig)) {
                await embedReport(refreshedConfig);
              }
            } catch (refreshError) {
              if (refreshError.name !== "AbortError" && active) {
                setError("Unable to refresh dashboard. Please reload the page.");
              }
            }
          }, delayMs);
        }
      } catch (e) {
        if (e.name !== "AbortError" && active) {
          setError("Unable to load dashboard. Please try again later.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      active = false;
      controller.abort();
      clearRefreshTimer();
      resetEmbed();
    };
  }, [clearRefreshTimer, embedReport, resetEmbed]);

  return (
    <Box className="wrapperBox">
      <Grid container>
        <NunitoText
          value="Dashboard"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
      </Grid>
      <Box style={{ position: "relative", marginTop: 24 }}>
        {loading && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              backgroundColor: "#FFF",
            }}
          >
            <Loader height="100%" />
          </Box>
        )}
        {error && !loading && <NoData height="70vh" text={error} />}
        <Box
          ref={containerRef}
          className="powerbi-report-container"
          style={{
            visibility: loading || error ? "hidden" : "visible",
          }}
        />
      </Box>
    </Box>
  );
}
